import React, { useCallback, useMemo, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import CustomColumnSelector from 'talent_matching/components/shared/custom_column_selector';
import {
  useColumnVisibility,
  NON_TOGGLABLE_COLUMNS,
} from 'talent_matching/hooks/use_column_visibility';
import { useMyGreenhouseColumns } from 'talent_matching/my_greenhouse/components/table/my_greenhouse_columns';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useMyGreenhouseTabContext } from 'talent_matching/contexts/my_greenhouse_tab_context';
import MyGreenhouseFilterRow from 'talent_matching/my_greenhouse/components/filters/my_greenhouse_filter_row';
import MyGreenhouseTableWrapper from 'talent_matching/my_greenhouse/components/table/my_greenhouse_table_wrapper';
import {
  MY_GREENHOUSE_DEFAULT_VALUES,
  type MyGreenhouseFilters,
} from 'talent_matching/my_greenhouse/my_greenhouse_default_values';
import isEqual from 'lodash/isEqual';
import SavedFilters from 'talent_matching/components/filters/saved_filters';
import { useSavedFilters } from 'talent_matching/hooks/use_saved_filters';
import {
  parseFormFieldsFromSavedSearch,
  toSavedSearchQueryString,
} from 'talent_matching/components/filters/utils';
import { formatCount } from 'talent_matching/components/header_row/candidate_count_helper';

const t = withLocalePrefix('talent_matching.header_row');

type MyGreenhousePageContentProps = {
  defaultValues: MyGreenhouseFilters;
};

const MY_GREENHOUSE_DEFAULT_COLUMN_VISIBILITY_MODEL: GridColumnVisibilityModel =
  {
    fullName: true,
    resumeDetails: true,
    matchScore: true,
    workPreferences: true,
  };

const MyGreenhousePageContent = ({
  defaultValues,
}: MyGreenhousePageContentProps) => {
  const {
    savedSearchOptions,
    setSavedSearchOptions,
    savedFilterSet,
    setSavedFilterSet,
  } = useMyGreenhouseTabContext();

  const { getValues, control } = useFormContext();
  const formValues = useWatch({ control });

  const [filteredTotalRows, setFilteredTotalRows] = useState<number>(0);

  const [normalizedColumnVisibilityModel, setColumnVisibilityModel] =
    useColumnVisibility({
      storageKey: 'my-greenhouse-column-visibility',
      defaults: MY_GREENHOUSE_DEFAULT_COLUMN_VISIBILITY_MODEL,
    });

  const buildQueryString = useCallback(() => {
    return toSavedSearchQueryString(getValues());
  }, [getValues]);

  const { savedFiltersProps, handleClearAll } = useSavedFilters({
    featureType: 'my_greenhouse_discovery',
    formDefaults: defaultValues as Record<string, unknown>,
    savedFilterSet,
    setSavedFilterSet,
    savedSearchOptions,
    setSavedSearchOptions,
    parseSavedSearch: (response: unknown) =>
      parseFormFieldsFromSavedSearch(response as Record<string, unknown>),
    buildQueryString,
  });

  const hasAnyFilters = useMemo(() => {
    const nonFilterFields = new Set(['page', 'perPage']);
    const defaults = MY_GREENHOUSE_DEFAULT_VALUES as Record<string, unknown>;
    const current = formValues as Record<string, unknown>;

    return Object.keys(defaults).some((key) => {
      if (nonFilterFields.has(key)) {
        return false;
      }
      if (key === 'companyNameFilters') {
        return ((current[key] ?? []) as { value: string }[]).some(
          (f) => f.value.trim() !== ''
        );
      }
      return !isEqual(current[key], defaults[key]);
    });
  }, [formValues]);

  const columns = useMyGreenhouseColumns();
  const columnSelectorColumns = useMemo(() => {
    return columns
      .filter((c) => !NON_TOGGLABLE_COLUMNS.includes(c.field))
      .map((c) => ({
        field: c.field,
        label: c.headerName || c.field,
      }));
  }, [columns]);

  return (
    <Stack spacing={0}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginTop: 0, marginBottom: 2 }}
      >
        <Body medium>
          {t('candidates', {
            count: filteredTotalRows,
            formattedCount: formatCount(filteredTotalRows),
          })}
        </Body>
        <CustomColumnSelector
          columns={columnSelectorColumns}
          columnVisibilityModel={normalizedColumnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
          defaultColumnVisibilityModel={
            MY_GREENHOUSE_DEFAULT_COLUMN_VISIBILITY_MODEL
          }
        />
      </Stack>

      <MyGreenhouseFilterRow onClearAll={handleClearAll}>
        <SavedFilters {...savedFiltersProps} />
      </MyGreenhouseFilterRow>

      <MyGreenhouseTableWrapper
        onTotalRowsChange={setFilteredTotalRows}
        columnVisibilityModel={normalizedColumnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        hasAnyFilters={hasAnyFilters}
        onClearAll={handleClearAll}
      />
    </Stack>
  );
};

export default MyGreenhousePageContent;
